/*
 * CUSTOMIZED · HYPOGEN Lieferzeitanzeige · Kalender, Version 3
 * Zieldatei: assets/hypogen-delivery-calendar.js
 * Zugehörigkeit: hypogen-delivery-config.liquid und
 * hypogen-delivery-estimate.js; Anzeige/Controller separat.
 *
 * Funktion: erster möglicher Bearbeitungs-/Versandtag anhand von
 * Lagerzeitzone, Bestellannahmeschluss, geöffneten Wochentagen,
 * optionalem NRW-Feiertagskalender, zusätzlichen Schließungen
 * und zusätzlichen offenen Bearbeitungstagen.
 *
 * Keine Änderung von Markets, Warenkorb, Checkout oder Shopify-
 * Versandeinstellungen. Kein Transport-/Zielland-Feiertagskalender.
 * Automatischer Feiertagskalender derzeit ausschließlich DE-NW.
 * Datumsschlüssel YYYY-MM-DD; vor Annahmeschluss aktueller Tag,
 * ab Annahmeschluss nächster offener Bearbeitungstag.
 */
(() => {
  'use strict';

  const dateKey = (date) => date.toISOString().slice(0, 10);

  function parseKey(key) {
    if (typeof key !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(key)) {
      throw new Error('Invalid date key');
    }

    const date = new Date(`${key}T00:00:00.000Z`);

    if (!Number.isFinite(date.getTime()) || dateKey(date) !== key) {
      throw new Error('Invalid date');
    }

    return date;
  }

  function addDays(key, days) {
    if (!Number.isInteger(days)) {
      throw new Error('Invalid day increment');
    }

    const date = parseKey(key);
    date.setUTCDate(date.getUTCDate() + days);
    return dateKey(date);
  }

  function easterSunday(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;

    return dateKey(new Date(Date.UTC(year, month - 1, day)));
  }

  function holidaysNRW(year) {
    const easter = easterSunday(year);

    return new Set([
      `${year}-01-01`,
      `${year}-05-01`,
      `${year}-10-03`,
      `${year}-11-01`,
      `${year}-12-25`,
      `${year}-12-26`,
      addDays(easter, -2),
      addDays(easter, 1),
      addDays(easter, 39),
      addDays(easter, 50),
      addDays(easter, 60)
    ]);
  }

  function normalizeOptions(input = {}) {
    // Compatibility with the previous calendar's array argument.
    const options = Array.isArray(input)
      ? { extraClosedDates: input }
      : input;

    if (!options || typeof options !== 'object') {
      throw new Error('Missing processing configuration');
    }

    const weekdays = options.weekdays ?? [1, 2, 3, 4, 5];
    const holidayRegion = options.holidayRegion ?? 'DE-NW';
    const extraClosedDates = options.extraClosedDates ?? [];

    if (
      !Array.isArray(weekdays) ||
      weekdays.length === 0 ||
      weekdays.some((day) => (
        !Number.isInteger(day) || day < 0 || day > 6
      ))
    ) {
      throw new Error('Select at least one valid processing weekday');
    }

    if (!['DE-NW', 'none'].includes(holidayRegion)) {
      throw new Error('Unsupported warehouse holiday calendar');
    }

    if (!Array.isArray(extraClosedDates)) {
      throw new Error('Invalid warehouse closure list');
    }

    extraClosedDates.forEach(parseKey);

    return {
      weekdays,
      holidayRegion,
      extraClosedDates
    };
  }

  function isProcessingDay(key, options = {}) {
    const settings = normalizeOptions(options);
    const date = parseKey(key);

    return (
      settings.weekdays.includes(date.getUTCDay()) &&
      !settings.extraClosedDates.includes(key) &&
      (
        settings.holidayRegion === 'none' ||
        !holidaysNRW(date.getUTCFullYear()).has(key)
      )
    );
  }

  function nextProcessingDay(key, options = {}) {
    const settings = normalizeOptions(options);
    let candidate = key;

    for (let attempts = 0; attempts < 370; attempts += 1) {
      if (isProcessingDay(candidate, settings)) {
        return candidate;
      }

      candidate = addDays(candidate, 1);
    }

    throw new Error('No open processing day within 370 days');
  }

  function localParts(instant, timeZone) {
    const date = new Date(instant);

    if (!Number.isFinite(date.getTime())) {
      throw new Error('Invalid instant');
    }

    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    }).formatToParts(date);

    const values = Object.fromEntries(
      parts.map((part) => [part.type, part.value])
    );

    return {
      key: `${values.year}-${values.month}-${values.day}`,
      minutes: Number(values.hour) * 60 + Number(values.minute)
    };
  }

  function cutoffMinutesFor(options) {
    if (options.cutoffTime !== undefined) {
      if (
        typeof options.cutoffTime !== 'string' ||
        !/^([01]\d|2[0-3]):[0-5]\d$/.test(options.cutoffTime)
      ) {
        throw new Error('Cut-off must use HH:MM');
      }

      const [hours, minutes] = options.cutoffTime
        .split(':')
        .map(Number);

      return hours * 60 + minutes;
    }

    const minutes = options.cutoffMinutes ?? 900;

    if (
      !Number.isInteger(minutes) ||
      minutes < 0 || minutes > 1439
    ) {
      throw new Error('Invalid cut-off');
    }

    return minutes;
  }

  function processingStart(instant = new Date(), options = {}) {
    const settings = normalizeOptions(options);
    const timeZone = options.timeZone ?? 'Europe/Berlin';
    const cutoffMinutes = cutoffMinutesFor(options);
    const processingDays = options.processingDays ?? 0;

    if (
      typeof timeZone !== 'string' || !timeZone.trim() ||
      !Number.isInteger(processingDays) ||
      processingDays < 0 || processingDays > 30
    ) {
      throw new Error('Invalid processing settings');
    }

    const local = localParts(instant, timeZone);
    const candidate = local.minutes >= cutoffMinutes
      ? addDays(local.key, 1)
      : local.key;

    let result = nextProcessingDay(candidate, settings);

    for (let day = 0; day < processingDays; day += 1) {
      result = nextProcessingDay(addDays(result, 1), settings);
    }

    return result;
  }

  globalThis.HypogenDeliveryCalendar = Object.freeze({
    addDays,
    holidaysNRW,
    isProcessingDay,
    nextProcessingDay,
    processingStart
  });
})();
